import React, { useState } from 'react';
import { Menu, X, Mail, Copy } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackNavigationClick } from './GoogleAnalytics';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    // Track navigation click
    trackNavigationClick(sectionId);
    
    // Se não estiver na página inicial, navegar para ela primeiro
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const targetId = sectionId === 'contato' ? 'formulario-contato' : sectionId;
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const targetId = sectionId === 'contato' ? 'formulario-contato' : sectionId;
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const navigateToPage = (path: string) => {
    // Track navigation click for special pages
    if (path === '/fisioterapia-musculo-esqueletica') {
      trackNavigationClick('fisioterapia-musculo-esqueletica');
    }
    
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Toast */}
      {showToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-[60] animate-fade-in">
          Email copiado para a área de transferência!
        </div>
      )}
      
      <header className="bg-white shadow-lg fixed w-full top-0 z-50">
      {/* Top bar com informações de contato */}
      <div className="bg-primary-500 text-white py-2 hidden sm:block">
        <div className="container-custom">
          <div className="flex justify-center items-center text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <a 
                href="mailto:contato@katiahondafisio.com.br"
                className="hover:text-primary-200 transition-colors"
              >
                contato@katiahondafisio.com.br
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('contato@katiahondafisio.com.br');
                  setShowToast(true);
                  setTimeout(() => setShowToast(false), 3000);
                }}
                className="p-1 hover:bg-primary-600 rounded transition-colors"
                title="Copiar e-mail"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navegação principal */}
      <nav className="bg-white py-4">
        <div className="container-custom px-4 sm:px-6">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-primary-600">Dra. Katia Honda</span>
              <span className="text-xs text-gray-500 -mt-1">
                 Fisioterapeuta Crefito-3 401508-F
              </span>
            </div>

            {/* Menu Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('inicio')}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-300 font-medium"
              >
                Início
              </button>
              <button
                onClick={() => scrollToSection('sobre')}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-300 font-medium"
              >
                Sobre
              </button>
              <button
                onClick={() => scrollToSection('servicos')}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-300 font-medium"
              >
                Serviços
              </button>
              <button
                onClick={() => navigateToPage('/fisioterapia-musculo-esqueletica')}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-300 font-medium"
              >
                Fisioterapia M.E.
              </button>
              <button
                onClick={() => scrollToSection('contato')}
                className="text-gray-700 hover:text-primary-500 transition-colors duration-300 font-medium"
              >
                Contato
              </button>
            </div>

            {/* Botão Menu Mobile */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-3 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Menu Mobile */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 px-2">
              <div className="flex flex-col space-y-2 pt-4">
                <button
                  onClick={() => scrollToSection('inicio')}
                  className="text-left text-gray-700 hover:text-primary-500 active:text-primary-600 active:bg-primary-50 transition-colors duration-300 font-medium py-4 px-4 rounded-lg hover:bg-gray-50 min-h-[48px] flex items-center touch-manipulation"
                >
                  Início
                </button>
                <button
                  onClick={() => scrollToSection('sobre')}
                  className="text-left text-gray-700 hover:text-primary-500 active:text-primary-600 active:bg-primary-50 transition-colors duration-300 font-medium py-4 px-4 rounded-lg hover:bg-gray-50 min-h-[48px] flex items-center touch-manipulation"
                >
                  Sobre
                </button>
                <button
                  onClick={() => scrollToSection('servicos')}
                  className="text-left text-gray-700 hover:text-primary-500 active:text-primary-600 active:bg-primary-50 transition-colors duration-300 font-medium py-4 px-4 rounded-lg hover:bg-gray-50 min-h-[48px] flex items-center touch-manipulation"
                >
                  Serviços
                </button>
                <button
                  onClick={() => navigateToPage('/fisioterapia-musculo-esqueletica')}
                  className="text-left text-gray-700 hover:text-primary-500 active:text-primary-600 active:bg-primary-50 transition-colors duration-300 font-medium py-4 px-4 rounded-lg hover:bg-gray-50 min-h-[48px] flex items-center touch-manipulation"
                >
                  Fisioterapia M.E.
                </button>
                <button
                  onClick={() => scrollToSection('contato')}
                  className="text-left text-gray-700 hover:text-primary-500 active:text-primary-600 active:bg-primary-50 transition-colors duration-300 font-medium py-4 px-4 rounded-lg hover:bg-gray-50 min-h-[48px] flex items-center touch-manipulation"
                >
                  Contato
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
    </>
  );
};

export default Header;